// =====================================================
// THEME PRESETS - Design Token System
// =====================================================
// Each theme is a self-contained config object.
// Add new themes here without touching any components.
// Set premium: true for future paid theme packs.
// =====================================================

export const FONT_OPTIONS = [
  { key: "retro", label: "Press Start 2P", heading: "'Press Start 2P', cursive", body: "'Share Tech Mono', monospace" },
  { key: "jetbrains", label: "JetBrains Mono", heading: "'JetBrains Mono', monospace", body: "'JetBrains Mono', monospace" },
  { key: "fira", label: "Fira Code", heading: "'Fira Code', monospace", body: "'Fira Code', monospace" },
  { key: "space", label: "Space Mono", heading: "'Space Mono', monospace", body: "'Space Mono', monospace" },
  { key: "inter", label: "Inter", heading: "'Inter', sans-serif", body: "'Inter', sans-serif" },
  { key: "ibm", label: "IBM Plex Mono", heading: "'IBM Plex Mono', monospace", body: "'IBM Plex Mono', monospace" },
  { key: "orbitron", label: "Orbitron", heading: "'Orbitron', sans-serif", body: "'Space Mono', monospace" },
  { key: "rajdhani", label: "Rajdhani", heading: "'Rajdhani', sans-serif", body: "'JetBrains Mono', monospace" },
  { key: "system", label: "System Default", heading: "system-ui, -apple-system, sans-serif", body: "system-ui, -apple-system, sans-serif" },
];

export const CARD_STYLES = ["bordered", "elevated", "glass", "flat"];
export const BUTTON_STYLES = ["outlined", "filled", "ghost"];
export const PROGRESS_STYLES = ["solid", "glow", "striped"];

export const DEFAULT_THEME_ID = "adhd-calm";

// --- Theme ID Migrations: legacy → current ---
// When a user's saved theme references a deprecated id, transparently
// substitute the new id at hydration time. Preserves the UX promise that
// "Dark" looks like the same Dark across releases.
export const THEME_ID_MIGRATIONS = {
  "mmntm-dark":       "minimalist-bw",       // old "Dark" preset → new monochrome Dark
  "mmntm-light":      "minimalist-bw-dark",  // legacy slate variant → Light
  "mmntm-light-true": "minimalist-bw-dark",  // old "Light" preset → new monochrome Light
};

export function migrateThemeId(id) {
  if (!id) return id;
  return THEME_ID_MIGRATIONS[id] || id;
}

// --- Theme Progression: Level requirements for unlock ---
// Themes with levelRequired: 0 are free/default
export const THEME_LEVEL_MAP = {
  "mmntm-dark": 0,
  "mmntm-light": 0,
  "mmntm-light-true": 0,
  "midnight-purple": 0,
  "deep-navy": 0,
  "forest": 0,
  "minimalist-bw": 0,
  "minimalist-bw-dark": 0,
  // Early levels (2-5): frequent choices
  "calm-focus": 2,
  "minimal-light": 2,
  "corporate-blue": 3,
  "neutral-workspace": 3,
  "soft-productivity": 4,
  "adhd-calm": 5,
  // Mid levels (6-10): refined themes
  "office-classic": 6,
  "executive-dark": 7,
  "green-ledger": 7,
  "warm-vintage": 8,
  "calm-corporate": 8,
  "focus-whiteboard": 9,
  "adhd-warm": 9,
  "adhd-ocean": 10,
  // Higher levels (11-20): premium themes
  "slate-pro": 11,
  "exec-dark-tech": 12,
  "high-contrast": 13,
  "industrial": 14,
  "hacker-ops": 15,
  "retro-cyber": 16,
  // Rare/exclusive (20+)
  "neon-arcade": 20,
  "gaming-hud": 22,
  "synthwave": 25,
};

// --- Shared base defaults ---
const DARK_BASE = {
  isDark: true,
  textPrimary: "#e0e0e0",
  textSecondary: "#999999",
  textMuted: "#555555",
  success: "#00ff41",
  danger: "#ff3333",
  warning: "#ff8800",
  progressStyle: "glow",
  buttonStyle: "outlined",
};

const LIGHT_BASE = {
  isDark: false,
  textPrimary: "#1a1a1a",
  textSecondary: "#555555",
  textMuted: "#999999",
  success: "#16a34a",
  danger: "#dc2626",
  warning: "#d97706",
  glowIntensity: 0,
  scanlines: false,
  progressStyle: "solid",
  buttonStyle: "filled",
};

// --- Professional bases (enforce corporate colour discipline) ---
const PRO_DARK_BASE = {
  isDark: true,
  textPrimary: "#F5F7FA",
  textSecondary: "#9CA3AF",
  textMuted: "#4A5568",
  success: "#2F855A",
  danger: "#C53030",
  warning: "#D69E2E",
  glowIntensity: 0,
  scanlines: false,
  progressStyle: "solid",
  buttonStyle: "outlined",
};

const PRO_LIGHT_BASE = {
  isDark: false,
  textPrimary: "#1F2933",
  textSecondary: "#4A5568",
  textMuted: "#9CA3AF",
  success: "#2F855A",
  danger: "#C53030",
  warning: "#D69E2E",
  glowIntensity: 0,
  scanlines: false,
  progressStyle: "solid",
  buttonStyle: "outlined",
};

// Approved professional accent palette for reference & user customisation
export const PROFESSIONAL_PALETTE = {
  primary: ["#FFFFFF", "#F5F7FA", "#1F2933", "#2D3748", "#000000"],
  secondary: ["#4A5568", "#6B7280", "#718096", "#9CA3AF"],
  accent: ["#1E3A8A", "#3B82F6", "#2C7A7B", "#2F855A", "#B7791F", "#4C51BF", "#475569"],
  status: { success: "#2F855A", warning: "#D69E2E", danger: "#C53030", info: "#4C51BF" },
};

// =====================================================
// CORE CURATED THEMES (new 2026 theme system)
// Expose as CURATED_CORE_THEMES below
// =====================================================

const midnightPurple = {
  id: "midnight-purple",
  name: "Midnight Purple",
  category: "mmntm",
  premium: false,
  description: "Deep violet command center — night-owl focus",
  styleType: "professional",
  preview: { bg: "#0F0A1A", accent: "#A855F7", accent2: "#C084FC" },
  tokens: {
    isDark: true,
    textPrimary: "#F5F3FF",
    textSecondary: "#C4B5FD",
    textMuted: "#6B5B95",
    success: "#22C55E",
    danger: "#EF4444",
    warning: "#F59E0B",
    glowIntensity: 0,
    scanlines: false,
    progressStyle: "solid",
    buttonStyle: "filled",
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Manrope', sans-serif",
    fontScale: 1.0,
    bgPrimary: "#0F0A1A",
    bgSecondary: "#1A1228",
    bgTertiary: "#231832",
    bgCard: "#181128",
    accent: "#A855F7",
    accent2: "#C084FC",
    accent3: "#EC4899",
    accent4: "#8B5CF6",
    spacing: 1.0,
    borderRadius: 6,
    borderColor: "#2A1E3D",
    borderWidth: 1,
    cardStyle: "bordered",
    headerBg: "#0F0A1A",
    headerBorder: "#2A1E3D",
  },
};

const deepNavy = {
  id: "deep-navy",
  name: "Deep Navy",
  category: "mmntm",
  premium: false,
  description: "Executive navy — corporate polish for deep work",
  styleType: "professional",
  preview: { bg: "#0B0F19", accent: "#3B82F6", accent2: "#60A5FA" },
  tokens: {
    isDark: true,
    textPrimary: "#F1F5F9",
    textSecondary: "#94A3B8",
    textMuted: "#475569",
    success: "#22C55E",
    danger: "#EF4444",
    warning: "#F59E0B",
    glowIntensity: 0,
    scanlines: false,
    progressStyle: "solid",
    buttonStyle: "filled",
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Manrope', sans-serif",
    fontScale: 1.0,
    bgPrimary: "#0B0F19",
    bgSecondary: "#131C2D",
    bgTertiary: "#1E293B",
    bgCard: "#131C2D",
    accent: "#3B82F6",
    accent2: "#60A5FA",
    accent3: "#06B6D4",
    accent4: "#6366F1",
    spacing: 1.0,
    borderRadius: 6,
    borderColor: "#1E293B",
    borderWidth: 1,
    cardStyle: "bordered",
    headerBg: "#0B0F19",
    headerBorder: "#1E293B",
  },
};

const forest = {
  id: "forest",
  name: "Forest",
  category: "mmntm",
  premium: false,
  description: "Natural greens — calm, grounded, focused",
  styleType: "professional",
  preview: { bg: "#0A1410", accent: "#22C55E", accent2: "#4ADE80" },
  tokens: {
    isDark: true,
    textPrimary: "#F0FDF4",
    textSecondary: "#86EFAC",
    textMuted: "#4D7C5A",
    success: "#22C55E",
    danger: "#EF4444",
    warning: "#F59E0B",
    glowIntensity: 0,
    scanlines: false,
    progressStyle: "solid",
    buttonStyle: "filled",
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Manrope', sans-serif",
    fontScale: 1.0,
    bgPrimary: "#0A1410",
    bgSecondary: "#0F1D17",
    bgTertiary: "#13281E",
    bgCard: "#0F1D17",
    accent: "#22C55E",
    accent2: "#4ADE80",
    accent3: "#10B981",
    accent4: "#84CC16",
    spacing: 1.0,
    borderRadius: 6,
    borderColor: "#1F3A2C",
    borderWidth: 1,
    cardStyle: "bordered",
    headerBg: "#0A1410",
    headerBorder: "#1F3A2C",
  },
};

// =====================================================
// MMNTM CORE THEMES (Brand Default)
// =====================================================

const mmntmDark = {
  id: "mmntm-dark",
  name: "MMNTM Core",
  category: "mmntm",
  premium: false,
  description: "Premium command center — the default MMNTM experience",
  styleType: "professional",
  preview: { bg: "#0B0F19", accent: "#3B82F6", accent2: "#6366F1" },
  tokens: {
    isDark: true,
    textPrimary: "#F8FAFC",
    textSecondary: "#94A3B8",
    textMuted: "#475569",
    success: "#22C55E",
    danger: "#EF4444",
    warning: "#F59E0B",
    glowIntensity: 0,
    scanlines: false,
    progressStyle: "solid",
    buttonStyle: "filled",
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Manrope', sans-serif",
    fontScale: 1.0,
    bgPrimary: "#0B0F19",
    bgSecondary: "#111827",
    bgTertiary: "#1E293B",
    bgCard: "#131C2D",
    accent: "#3B82F6",
    accent2: "#6366F1",
    accent3: "#EC4899",
    accent4: "#06B6D4",
    spacing: 1.0,
    borderRadius: 6,
    borderColor: "#1E293B",
    borderWidth: 1,
    cardStyle: "bordered",
    headerBg: "#0B0F19",
    headerBorder: "#1E293B",
  },
};

const mmntmLight = {
  id: "mmntm-light",
  name: "MMNTM Slate",
  category: "mmntm",
  premium: false,
  description: "Slate variant — same command center, slightly lighter surface",
  styleType: "professional",
  preview: { bg: "#0F1623", accent: "#3B82F6", accent2: "#6366F1" },
  tokens: {
    isDark: true,
    textPrimary: "#F1F5F9",
    textSecondary: "#94A3B8",
    textMuted: "#475569",
    success: "#22C55E",
    danger: "#EF4444",
    warning: "#F59E0B",
    glowIntensity: 0,
    scanlines: false,
    progressStyle: "solid",
    buttonStyle: "filled",
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Manrope', sans-serif",
    fontScale: 1.0,
    bgPrimary: "#0F1623",
    bgSecondary: "#162032",
    bgTertiary: "#1E293B",
    bgCard: "#162032",
    accent: "#3B82F6",
    accent2: "#6366F1",
    accent3: "#EC4899",
    accent4: "#06B6D4",
    spacing: 1.0,
    borderRadius: 6,
    borderColor: "#1E293B",
    borderWidth: 1,
    cardStyle: "bordered",
    headerBg: "#0F1623",
    headerBorder: "#1E293B",
  },
};

// MMNTM box colors
export const BOX_COLORS_MMNTM_DARK = {
  green:  { screen: "#0B1A14", text: "#22C55E", dim: "#16A34A60", glow: "none", border: "#16332A" },
  amber:  { screen: "#1A160B", text: "#F59E0B", dim: "#D9770660", glow: "none", border: "#332E16" },
  cyan:   { screen: "#0B171A", text: "#06B6D4", dim: "#0891B260", glow: "none", border: "#163033" },
  pink:   { screen: "#1A0B17", text: "#EC4899", dim: "#DB277760", glow: "none", border: "#331630" },
  purple: { screen: "#140B1F", text: "#8B5CF6", dim: "#7C3AED60", glow: "none", border: "#2A163D" },
  orange: { screen: "#1A140B", text: "#F97316", dim: "#EA580C60", glow: "none", border: "#332916" },
  yellow: { screen: "#1A190B", text: "#EAB308", dim: "#CA8A0460", glow: "none", border: "#333116" },
  white:  { screen: "#131C2D", text: "#94A3B8", dim: "#64748B60", glow: "none", border: "#1E293B" },
};

export const BOX_COLORS_MMNTM_LIGHT = {
  green:  { screen: "#F0FDF4", text: "#16A34A", dim: "#22C55E40", glow: "none", border: "#BBF7D0" },
  amber:  { screen: "#FFFBEB", text: "#D97706", dim: "#F59E0B40", glow: "none", border: "#FDE68A" },
  cyan:   { screen: "#ECFEFF", text: "#0891B2", dim: "#06B6D440", glow: "none", border: "#A5F3FC" },
  pink:   { screen: "#FDF2F8", text: "#DB2777", dim: "#EC489940", glow: "none", border: "#FBCFE8" },
  purple: { screen: "#FAF5FF", text: "#7C3AED", dim: "#8B5CF640", glow: "none", border: "#E9D5FF" },
  orange: { screen: "#FFF7ED", text: "#EA580C", dim: "#F9731640", glow: "none", border: "#FED7AA" },
  yellow: { screen: "#FEFCE8", text: "#CA8A04", dim: "#EAB30840", glow: "none", border: "#FEF08A" },
  white:  { screen: "#F9FAFB", text: "#52525B", dim: "#A1A1AA40", glow: "none", border: "#E4E4E7" },
};

// =====================================================
// CREATIVE THEMES (10)
// =====================================================

const retroCyber = {
  id: "retro-cyber",
  name: "Retro Cyber Terminal",
  category: "creative",
  premium: false,
  description: "Classic CRT workstation with phosphor glow",
  preview: { bg: "#060606", accent: "#00ff41", accent2: "#00f0ff" },
  tokens: {
    ...DARK_BASE,
    fontHeading: "'Press Start 2P', cursive",
    fontBody: "'Share Tech Mono', monospace",
    fontScale: 1.0,
    bgPrimary: "#060606",
    bgSecondary: "#111111",
    bgTertiary: "#181818",
    bgCard: "#0a0a0a",
    accent: "#00ff41",
    accent2: "#00f0ff",
    accent3: "#ffb000",
    accent4: "#bf00ff",
    glowIntensity: 1.0,
    scanlines: true,
    spacing: 1.0,
    borderRadius: 4,
    borderColor: "#222222",
    borderWidth: 1,
    cardStyle: "bordered",
    headerBg: "linear-gradient(180deg, #141414 0%, #0a0a0a 100%)",
    headerBorder: "#222222",
  },
};

const neonArcade = {
  id: "neon-arcade",
  name: "Neon Arcade",
  category: "creative",
  premium: false,
  description: "Hot pink and electric blue arcade vibes",
  preview: { bg: "#0a0015", accent: "#ff00ff", accent2: "#00ffff" },
  tokens: {
    ...DARK_BASE,
    fontHeading: "'Orbitron', sans-serif",
    fontBody: "'Space Mono', monospace",
    fontScale: 1.0,
    bgPrimary: "#0a0015",
    bgSecondary: "#150025",
    bgTertiary: "#1a0030",
    bgCard: "#0d001a",
    accent: "#ff00ff",
    accent2: "#00ffff",
    accent3: "#ffff00",
    accent4: "#ff3366",
    glowIntensity: 1.2,
    scanlines: false,
    spacing: 1.0,
    borderRadius: 12,
    borderColor: "#2a0050",
    borderWidth: 2,
    cardStyle: "elevated",
    headerBg: "linear-gradient(180deg, #1a0030 0%, #0a0015 100%)",
    headerBorder: "#2a0050",
  },
};

const hackerOps = {
  id: "hacker-ops",
  name: "Hacker Ops",
  category: "creative",
  premium: false,
  description: "Green-on-black monochrome terminal",
  preview: { bg: "#000000", accent: "#00ff00", accent2: "#00cc00" },
  tokens: {
    ...DARK_BASE,
    fontHeading: "'Fira Code', monospace",
    fontBody: "'Fira Code', monospace",
    fontScale: 1.0,
    textPrimary: "#00dd00",
    textSecondary: "#009900",
    textMuted: "#005500",
    bgPrimary: "#000000",
    bgSecondary: "#0a0a0a",
    bgTertiary: "#0f0f0f",
    bgCard: "#050505",
    accent: "#00ff00",
    accent2: "#00cc00",
    accent3: "#009900",
    accent4: "#33ff33",
    glowIntensity: 0.8,
    scanlines: true,
    spacing: 0.95,
    borderRadius: 0,
    borderColor: "#0f0f0f",
    borderWidth: 1,
    cardStyle: "bordered",
    headerBg: "linear-gradient(180deg, #0a0a0a 0%, #000000 100%)",
    headerBorder: "#0f0f0f",
  },
};

const synthwave = {
  id: "synthwave",
  name: "Synthwave",
  category: "creative",
  premium: false,
  description: "Purple sunset gradients with pink neon",
  preview: { bg: "#1a0530", accent: "#ff6ec7", accent2: "#00d4ff" },
  tokens: {
    ...DARK_BASE,
    fontHeading: "'Orbitron', sans-serif",
    fontBody: "'Inter', sans-serif",
    fontScale: 1.0,
    bgPrimary: "#1a0530",
    bgSecondary: "#260845",
    bgTertiary: "#2d0a52",
    bgCard: "#1e063a",
    accent: "#ff6ec7",
    accent2: "#00d4ff",
    accent3: "#ffb347",
    accent4: "#a855f7",
    glowIntensity: 0.9,
    scanlines: false,
    spacing: 1.05,
    borderRadius: 8,
    borderColor: "#3d1070",
    borderWidth: 1,
    cardStyle: "glass",
    headerBg: "linear-gradient(180deg, #2d0a52 0%, #1a0530 100%)",
    headerBorder: "#3d1070",
  },
};

const warmVintage = {
  id: "warm-vintage",
  name: "Warm Vintage Tech",
  category: "creative",
  premium: false,
  description: "Amber console with warm phosphor glow",
  preview: { bg: "#1c1410", accent: "#f0a050", accent2: "#d4956a" },
  tokens: {
    ...DARK_BASE,
    fontHeading: "'IBM Plex Mono', monospace",
    fontBody: "'IBM Plex Mono', monospace",
    fontScale: 1.0,
    textPrimary: "#e8d0b0",
    textSecondary: "#a08060",
    textMuted: "#604830",
    bgPrimary: "#1c1410",
    bgSecondary: "#2a1f18",
    bgTertiary: "#302418",
    bgCard: "#201810",
    accent: "#f0a050",
    accent2: "#d4956a",
    accent3: "#c07040",
    accent4: "#e8c080",
    success: "#80c040",
    danger: "#d05030",
    warning: "#e0a030",
    glowIntensity: 0.4,
    scanlines: true,
    spacing: 1.05,
    borderRadius: 4,
    borderColor: "#3a2a1a",
    borderWidth: 1,
    cardStyle: "bordered",
    headerBg: "linear-gradient(180deg, #302418 0%, #1c1410 100%)",
    headerBorder: "#3a2a1a",
  },
};

const industrial = {
  id: "industrial",
  name: "Industrial Control Room",
  category: "creative",
  premium: false,
  description: "Steel grey with amber warning indicators",
  preview: { bg: "#1a1c20", accent: "#ff8c00", accent2: "#ff4444" },
  tokens: {
    ...DARK_BASE,
    fontHeading: "'Rajdhani', sans-serif",
    fontBody: "'JetBrains Mono', monospace",
    fontScale: 1.0,
    bgPrimary: "#1a1c20",
    bgSecondary: "#22252a",
    bgTertiary: "#282c32",
    bgCard: "#1e2126",
    accent: "#ff8c00",
    accent2: "#ff4444",
    accent3: "#ffd700",
    accent4: "#44ff44",
    glowIntensity: 0.5,
    scanlines: false,
    spacing: 1.0,
    borderRadius: 2,
    borderColor: "#3a3c40",
    borderWidth: 2,
    cardStyle: "bordered",
    headerBg: "linear-gradient(180deg, #282c32 0%, #1a1c20 100%)",
    headerBorder: "#3a3c40",
  },
};

const gamingHud = {
  id: "gaming-hud",
  name: "Tactical HUD",
  category: "creative",
  premium: false,
  description: "Military-grade tactical interface with amber accents",
  preview: { bg: "#0A0A0A", accent: "#F59E0B", accent2: "#10B981" },
  tokens: {
    ...DARK_BASE,
    fontHeading: "'Barlow Condensed', sans-serif",
    fontBody: "'JetBrains Mono', monospace",
    fontScale: 1.0,
    textPrimary: "#FFFFFF",
    textSecondary: "#A3A3A3",
    textMuted: "#525252",
    success: "#10B981",
    danger: "#DC2626",
    warning: "#F59E0B",
    bgPrimary: "#0A0A0A",
    bgSecondary: "#141414",
    bgTertiary: "#1A1A1A",
    bgCard: "#111111",
    accent: "#F59E0B",
    accent2: "#10B981",
    accent3: "#DC2626",
    accent4: "#3B82F6",
    glowIntensity: 0.3,
    scanlines: true,
    spacing: 0.95,
    borderRadius: 2,
    borderColor: "#262626",
    borderWidth: 1,
    cardStyle: "bordered",
    headerBg: "#0A0A0A",
    headerBorder: "#262626",
  },
};

const calmFocus = {
  id: "calm-focus",
  name: "Calm Focus",
  category: "creative",
  premium: false,
  description: "Deep navy with soft blue and sage accents",
  preview: { bg: "#0f172a", accent: "#38bdf8", accent2: "#34d399" },
  tokens: {
    ...DARK_BASE,
    fontHeading: "'Inter', sans-serif",
    fontBody: "'Inter', sans-serif",
    fontScale: 1.0,
    textPrimary: "#e2e8f0",
    textSecondary: "#94a3b8",
    textMuted: "#475569",
    bgPrimary: "#0f172a",
    bgSecondary: "#1e293b",
    bgTertiary: "#1e293b",
    bgCard: "#172033",
    accent: "#38bdf8",
    accent2: "#34d399",
    accent3: "#a78bfa",
    accent4: "#fbbf24",
    success: "#34d399",
    danger: "#f87171",
    warning: "#fbbf24",
    glowIntensity: 0.2,
    scanlines: false,
    spacing: 1.1,
    borderRadius: 12,
    borderColor: "#334155",
    borderWidth: 1,
    cardStyle: "elevated",
    headerBg: "linear-gradient(180deg, #1e293b 0%, #0f172a 100%)",
    headerBorder: "#334155",
  },
};

const minimalLight = {
  id: "minimal-light",
  name: "Minimal Light",
  category: "creative",
  premium: false,
  description: "Clean white with subtle accents",
  preview: { bg: "#f5f5f5", accent: "#2563eb", accent2: "#059669" },
  tokens: {
    ...LIGHT_BASE,
    fontHeading: "'Inter', sans-serif",
    fontBody: "'Inter', sans-serif",
    fontScale: 1.0,
    bgPrimary: "#f5f5f5",
    bgSecondary: "#ffffff",
    bgTertiary: "#ffffff",
    bgCard: "#ffffff",
    accent: "#2563eb",
    accent2: "#059669",
    accent3: "#d97706",
    accent4: "#9333ea",
    borderRadius: 10,
    borderColor: "#e5e7eb",
    borderWidth: 1,
    cardStyle: "elevated",
    spacing: 1.05,
    headerBg: "#ffffff",
    headerBorder: "#e5e7eb",
  },
};

const execDarkTech = {
  id: "exec-dark-tech",
  name: "Clean Executive Dark",
  category: "creative",
  premium: false,
  description: "Sleek dark with muted blue tech accents",
  preview: { bg: "#1a1a2e", accent: "#3B82F6", accent2: "#2C7A7B" },
  tokens: {
    ...PRO_DARK_BASE,
    fontHeading: "'Inter', sans-serif",
    fontBody: "'Inter', sans-serif",
    fontScale: 1.0,
    textPrimary: "#F5F7FA",
    textSecondary: "#9CA3AF",
    textMuted: "#4A5568",
    bgPrimary: "#1a1a2e",
    bgSecondary: "#222240",
    bgTertiary: "#28284a",
    bgCard: "#1e1e38",
    accent: "#3B82F6",
    accent2: "#2C7A7B",
    accent3: "#B7791F",
    accent4: "#4C51BF",
    success: "#2F855A",
    danger: "#C53030",
    warning: "#D69E2E",
    glowIntensity: 0,
    scanlines: false,
    spacing: 1.05,
    borderRadius: 8,
    borderColor: "#333355",
    borderWidth: 1,
    cardStyle: "elevated",
    headerBg: "linear-gradient(180deg, #28284a 0%, #1a1a2e 100%)",
    headerBorder: "#333355",
  },
};

// =====================================================
// PROFESSIONAL THEMES (10)
// =====================================================

const corporateBlue = {
  id: "corporate-blue",
  name: "Corporate Steel",
  category: "professional",
  premium: false,
  description: "Steel blue command center — enterprise-grade",
  preview: { bg: "#0C1220", accent: "#1E3A8A", accent2: "#3B82F6" },
  tokens: {
    ...PRO_DARK_BASE,
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Manrope', sans-serif",
    fontScale: 1.0,
    textPrimary: "#E2E8F0",
    textSecondary: "#94A3B8",
    textMuted: "#475569",
    bgPrimary: "#0C1220",
    bgSecondary: "#111B2E",
    bgTertiary: "#1A2740",
    bgCard: "#111B2E",
    accent: "#1E3A8A",
    accent2: "#3B82F6",
    accent3: "#2C7A7B",
    accent4: "#4C51BF",
    success: "#16A34A",
    danger: "#DC2626",
    warning: "#D97706",
    borderRadius: 6,
    borderColor: "#1A2740",
    borderWidth: 1,
    cardStyle: "bordered",
    spacing: 1.0,
    headerBg: "#0C1220",
    headerBorder: "#1A2740",
  },
};

const executiveDark = {
  id: "executive-dark",
  name: "Executive Dark",
  category: "professional",
  premium: false,
  description: "Dark navy charcoal, sleek premium look",
  preview: { bg: "#111827", accent: "#3B82F6", accent2: "#4C51BF" },
  tokens: {
    ...PRO_DARK_BASE,
    fontHeading: "'Inter', sans-serif",
    fontBody: "'Inter', sans-serif",
    fontScale: 1.0,
    textPrimary: "#F5F7FA",
    textSecondary: "#9CA3AF",
    textMuted: "#4A5568",
    bgPrimary: "#111827",
    bgSecondary: "#1f2937",
    bgTertiary: "#1f2937",
    bgCard: "#1a2332",
    accent: "#3B82F6",
    accent2: "#4C51BF",
    accent3: "#B7791F",
    accent4: "#2C7A7B",
    success: "#2F855A",
    danger: "#C53030",
    warning: "#D69E2E",
    glowIntensity: 0,
    scanlines: false,
    spacing: 1.05,
    borderRadius: 8,
    borderColor: "#374151",
    borderWidth: 1,
    cardStyle: "elevated",
    headerBg: "linear-gradient(180deg, #1f2937 0%, #111827 100%)",
    headerBorder: "#374151",
  },
};

const neutralWorkspace = {
  id: "neutral-workspace",
  name: "Neutral Ops",
  category: "professional",
  premium: false,
  description: "Muted grey ops center — zero distraction",
  preview: { bg: "#111318", accent: "#64748B", accent2: "#94A3B8" },
  tokens: {
    ...PRO_DARK_BASE,
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Manrope', sans-serif",
    fontScale: 1.0,
    textPrimary: "#E2E8F0",
    textSecondary: "#94A3B8",
    textMuted: "#475569",
    bgPrimary: "#111318",
    bgSecondary: "#181C24",
    bgTertiary: "#1E2430",
    bgCard: "#181C24",
    accent: "#64748B",
    accent2: "#94A3B8",
    accent3: "#2DD4BF",
    accent4: "#818CF8",
    success: "#22C55E",
    danger: "#EF4444",
    warning: "#F59E0B",
    borderRadius: 4,
    borderColor: "#1E2430",
    borderWidth: 1,
    cardStyle: "bordered",
    spacing: 1.0,
    headerBg: "#111318",
    headerBorder: "#1E2430",
  },
};

const softProductivity = {
  id: "soft-productivity",
  name: "Deep Focus",
  category: "professional",
  premium: false,
  description: "Deep charcoal with warm muted accents",
  preview: { bg: "#0E1117", accent: "#64748B", accent2: "#22C55E" },
  tokens: {
    ...PRO_DARK_BASE,
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Manrope', sans-serif",
    fontScale: 1.0,
    textPrimary: "#E2E8F0",
    textSecondary: "#94A3B8",
    textMuted: "#475569",
    bgPrimary: "#0E1117",
    bgSecondary: "#161B24",
    bgTertiary: "#1C2230",
    bgCard: "#161B24",
    accent: "#64748B",
    accent2: "#22C55E",
    accent3: "#F59E0B",
    accent4: "#2DD4BF",
    success: "#22C55E",
    danger: "#EF4444",
    warning: "#F59E0B",
    borderRadius: 6,
    borderColor: "#1C2230",
    borderWidth: 1,
    cardStyle: "bordered",
    spacing: 1.0,
    headerBg: "#0E1117",
    headerBorder: "#1C2230",
  },
};

const officeClassic = {
  id: "office-classic",
  name: "Navy Command",
  category: "professional",
  premium: false,
  description: "Classic navy + white command interface",
  preview: { bg: "#0A1628", accent: "#1E3A8A", accent2: "#3B82F6" },
  tokens: {
    ...PRO_DARK_BASE,
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Manrope', sans-serif",
    fontScale: 1.0,
    textPrimary: "#E2E8F0",
    textSecondary: "#94A3B8",
    textMuted: "#475569",
    bgPrimary: "#0A1628",
    bgSecondary: "#0F1D34",
    bgTertiary: "#162544",
    bgCard: "#0F1D34",
    accent: "#1E3A8A",
    accent2: "#3B82F6",
    accent3: "#2DD4BF",
    accent4: "#818CF8",
    success: "#22C55E",
    danger: "#EF4444",
    warning: "#F59E0B",
    borderRadius: 4,
    borderColor: "#162544",
    borderWidth: 1,
    cardStyle: "bordered",
    spacing: 1.0,
    headerBg: "#0A1628",
    headerBorder: "#162544",
  },
};

const slatePro = {
  id: "slate-pro",
  name: "Slate Command",
  category: "professional",
  premium: false,
  description: "Dark slate with muted blue command tones",
  preview: { bg: "#141A24", accent: "#6B8CC7", accent2: "#94A3B8" },
  tokens: {
    ...PRO_DARK_BASE,
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Manrope', sans-serif",
    fontScale: 1.0,
    textPrimary: "#E2E8F0",
    textSecondary: "#94A3B8",
    textMuted: "#475569",
    bgPrimary: "#141A24",
    bgSecondary: "#1A2234",
    bgTertiary: "#222C40",
    bgCard: "#1A2234",
    accent: "#6B8CC7",
    accent2: "#94A3B8",
    accent3: "#F59E0B",
    accent4: "#2DD4BF",
    success: "#22C55E",
    danger: "#EF4444",
    warning: "#F59E0B",
    glowIntensity: 0,
    scanlines: false,
    spacing: 1.0,
    borderRadius: 6,
    borderColor: "#222C40",
    borderWidth: 1,
    cardStyle: "bordered",
    headerBg: "#141A24",
    headerBorder: "#222C40",
  },
};

const greenLedger = {
  id: "green-ledger",
  name: "Terminal Green",
  category: "professional",
  premium: false,
  description: "Financial terminal — dark green on black",
  preview: { bg: "#0A120E", accent: "#22C55E", accent2: "#16A34A" },
  tokens: {
    ...PRO_DARK_BASE,
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Manrope', sans-serif",
    fontScale: 1.0,
    textPrimary: "#D1FAE5",
    textSecondary: "#6EE7B7",
    textMuted: "#34D399",
    bgPrimary: "#0A120E",
    bgSecondary: "#0F1A14",
    bgTertiary: "#16231C",
    bgCard: "#0F1A14",
    accent: "#22C55E",
    accent2: "#16A34A",
    accent3: "#F59E0B",
    accent4: "#64748B",
    success: "#22C55E",
    danger: "#EF4444",
    warning: "#F59E0B",
    borderRadius: 2,
    borderColor: "#16231C",
    borderWidth: 1,
    cardStyle: "bordered",
    spacing: 1.0,
    headerBg: "#0A120E",
    headerBorder: "#16231C",
  },
};

const focusWhiteboard = {
  id: "focus-whiteboard",
  name: "Focus Dark",
  category: "professional",
  premium: false,
  description: "Pure dark focus mode with indigo accents",
  preview: { bg: "#0C0C14", accent: "#6366f1", accent2: "#8b5cf6" },
  tokens: {
    ...PRO_DARK_BASE,
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Manrope', sans-serif",
    fontScale: 1.0,
    textPrimary: "#E2E8F0",
    textSecondary: "#94A3B8",
    textMuted: "#475569",
    bgPrimary: "#0C0C14",
    bgSecondary: "#12121E",
    bgTertiary: "#1A1A2A",
    bgCard: "#12121E",
    accent: "#6366f1",
    accent2: "#8b5cf6",
    accent3: "#06b6d4",
    accent4: "#f59e0b",
    borderRadius: 8,
    borderColor: "#1A1A2A",
    borderWidth: 1,
    cardStyle: "bordered",
    spacing: 1.0,
    headerBg: "#0C0C14",
    headerBorder: "#1A1A2A",
  },
};

const calmCorporate = {
  id: "calm-corporate",
  name: "Calm Charcoal",
  category: "professional",
  premium: false,
  description: "Muted charcoal with slate blue accents",
  preview: { bg: "#121620", accent: "#475569", accent2: "#64748B" },
  tokens: {
    ...PRO_DARK_BASE,
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Manrope', sans-serif",
    fontScale: 1.0,
    textPrimary: "#E2E8F0",
    textSecondary: "#94A3B8",
    textMuted: "#475569",
    bgPrimary: "#121620",
    bgSecondary: "#181E2C",
    bgTertiary: "#1E2638",
    bgCard: "#181E2C",
    accent: "#475569",
    accent2: "#64748B",
    accent3: "#2DD4BF",
    accent4: "#818CF8",
    success: "#22C55E",
    danger: "#EF4444",
    warning: "#F59E0B",
    borderRadius: 6,
    borderColor: "#1E2638",
    borderWidth: 1,
    cardStyle: "bordered",
    spacing: 1.0,
    headerBg: "#121620",
    headerBorder: "#1E2638",
  },
};

const highContrast = {
  id: "high-contrast",
  name: "High Contrast Pro",
  category: "professional",
  premium: false,
  description: "Accessibility-focused strong black/white contrast",
  preview: { bg: "#000000", accent: "#FFFFFF", accent2: "#D69E2E" },
  tokens: {
    ...PRO_DARK_BASE,
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Manrope', sans-serif",
    fontScale: 1.1,
    textPrimary: "#FFFFFF",
    textSecondary: "#D1D5DB",
    textMuted: "#6B7280",
    bgPrimary: "#000000",
    bgSecondary: "#0A0A0A",
    bgTertiary: "#111111",
    bgCard: "#0A0A0A",
    accent: "#FFFFFF",
    accent2: "#D69E2E",
    accent3: "#2F855A",
    accent4: "#2C7A7B",
    success: "#2F855A",
    danger: "#C53030",
    warning: "#D69E2E",
    glowIntensity: 0,
    scanlines: false,
    spacing: 1.1,
    borderRadius: 4,
    borderColor: "#555555",
    borderWidth: 2,
    cardStyle: "bordered",
    headerBg: "#1a1a1a",
    headerBorder: "#555555",
  },
};

// True light MMNTM — clean bright canvas for daylight work
const mmntmLightTrue = {
  id: "mmntm-light-true",
  name: "MMNTM Light",
  category: "mmntm",
  premium: false,
  description: "Bright daylight canvas — crisp and readable",
  styleType: "professional",
  preview: { bg: "#FFFFFF", accent: "#3B82F6", accent2: "#6366F1" },
  tokens: {
    isDark: false,
    textPrimary: "#0F172A",
    textSecondary: "#475569",
    textMuted: "#94A3B8",
    success: "#16A34A",
    danger: "#DC2626",
    warning: "#D97706",
    glowIntensity: 0,
    scanlines: false,
    progressStyle: "solid",
    buttonStyle: "filled",
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Manrope', sans-serif",
    fontScale: 1.0,
    bgPrimary: "#FFFFFF",
    bgSecondary: "#F8FAFC",
    bgTertiary: "#F1F5F9",
    bgCard: "#FFFFFF",
    accent: "#3B82F6",
    accent2: "#6366F1",
    accent3: "#EC4899",
    accent4: "#06B6D4",
    spacing: 1.0,
    borderRadius: 6,
    borderColor: "#E2E8F0",
    borderWidth: 1,
    cardStyle: "bordered",
    headerBg: "#FFFFFF",
    headerBorder: "#E2E8F0",
  },
};

// =====================================================
// MINIMALIST B&W THEMES
// =====================================================

const minimalistBW = {
  id: "minimalist-bw",
  name: "Dark",
  category: "mmntm",
  premium: false,
  description: "Pure black monochrome — true-black command interface with no distractions",
  preview: { bg: "#000000", accent: "#FFFFFF", accent2: "#BBBBBB" },
  tokens: {
    isDark: true,
    // Bundle H — contrast bump. textSecondary was #BBBBBB which the user
    // tester read as "grey, hard to read" against pure-black. Lifted to
    // #E5E5E5 (~14:1 vs #000) so primary body copy reads near-white.
    // textMuted stays at #888888 for genuinely de-emphasised captions.
    textPrimary: "#FFFFFF",
    textSecondary: "#E5E5E5",
    textMuted: "#999999",
    success: "#FFFFFF",
    danger: "#FFFFFF",
    warning: "#BBBBBB",
    glowIntensity: 0,
    scanlines: false,
    progressStyle: "solid",
    buttonStyle: "outlined",
    fontHeading: "'Outfit', sans-serif",
    fontBody: "'Manrope', sans-serif",
    fontScale: 1.0,
    bgPrimary: "#000000",
    bgSecondary: "#000000",
    bgTertiary: "#0A0A0A",
    bgCard: "#000000",
    accent: "#FFFFFF",
    accent2: "#BBBBBB",
    accent3: "#888888",
    accent4: "#BBBBBB",
    spacing: 1.2,
    borderRadius: 0,
    borderColor: "#2A2A2A",
    borderWidth: 1,
    cardStyle: "flat",
    headerBg: "#000000",
    headerBorder: "#2A2A2A",
  },
};

const minimalistBWLight = {
  id: "minimalist-bw-dark",
  name: "Light",
  category: "mmntm",
  premium: false,
  description: "Clean true-white canvas — high-contrast monochrome for daylight focus",
  preview: { bg: "#FFFFFF", accent: "#000000", accent2: "#666666" },
  tokens: {
    isDark: false,
    textPrimary: "#000000",
    textSecondary: "#444444",
    textMuted: "#999999",
    success: "#000000",
    danger: "#000000",
    warning: "#666666",
    glowIntensity: 0,
    scanlines: false,
    progressStyle: "solid",
    buttonStyle: "outlined",
    fontHeading: "'Satoshi', sans-serif",
    fontBody: "'Satoshi', sans-serif",
    fontScale: 1.0,
    bgPrimary: "#FFFFFF",
    bgSecondary: "#F5F5F5",
    bgTertiary: "#EEEEEE",
    bgCard: "#F5F5F5",
    accent: "#000000",
    accent2: "#666666",
    accent3: "#999999",
    accent4: "#444444",
    spacing: 1.2,
    borderRadius: 0,
    borderColor: "#E0E0E0",
    borderWidth: 1,
    cardStyle: "bordered",
    headerBg: "#F5F5F5",
    headerBorder: "#E0E0E0",
  },
};

// =====================================================
// ADHD THEMES
// =====================================================

const adhdCalm = {
  id: "adhd-calm",
  name: "Calm Focus",
  category: "adhd",
  premium: false,
  description: "Dark calming tones — minimal distractions",
  preview: { bg: "#0F1418", accent: "#0D9488", accent2: "#F59E0B" },
  tokens: {
    isDark: true,
    // Bundle H — body-text contrast bump. textSecondary was #94A3B8
    // (~5.6:1 vs bg) which user-tested as "very hard to read"; lifted to
    // #CBD5E1 (~10:1 vs bg) so primary body copy is comfortably white-ish
    // while textMuted (#475569) still differentiates captions/timestamps.
    textPrimary: "#F5F7FA",
    textSecondary: "#CBD5E1",
    textMuted: "#64748B",
    success: "#059669",
    danger: "#DC2626",
    warning: "#D97706",
    glowIntensity: 0,
    scanlines: false,
    progressStyle: "solid",
    buttonStyle: "filled",
    fontHeading: "'Lexend', sans-serif",
    fontBody: "'Lexend', sans-serif",
    fontScale: 1.15,
    bgPrimary: "#0F1418",
    bgSecondary: "#161C22",
    bgTertiary: "#1C242E",
    bgCard: "#161C22",
    accent: "#0D9488",
    accent2: "#F59E0B",
    accent3: "#6366F1",
    accent4: "#EC4899",
    spacing: 1.2,
    borderRadius: 10,
    borderColor: "#1C242E",
    borderWidth: 1,
    cardStyle: "bordered",
    headerBg: "#0F1418",
    headerBorder: "#1C242E",
  },
};

const adhdWarm = {
  id: "adhd-warm",
  name: "Warm Cocoa",
  category: "adhd",
  premium: false,
  description: "Dark earthy tones — gentle on the mind",
  preview: { bg: "#141210", accent: "#B45309", accent2: "#059669" },
  tokens: {
    isDark: true,
    // Bundle H — contrast bump. See adhd-calm comment.
    textPrimary: "#F5F2EE",
    textSecondary: "#D6D3D1",
    textMuted: "#78716C",
    success: "#059669",
    danger: "#DC2626",
    warning: "#D97706",
    glowIntensity: 0,
    scanlines: false,
    progressStyle: "solid",
    buttonStyle: "filled",
    fontHeading: "'Lexend', sans-serif",
    fontBody: "'Lexend', sans-serif",
    fontScale: 1.15,
    bgPrimary: "#141210",
    bgSecondary: "#1C1916",
    bgTertiary: "#24211E",
    bgCard: "#1C1916",
    accent: "#B45309",
    accent2: "#059669",
    accent3: "#7C3AED",
    accent4: "#DB2777",
    spacing: 1.2,
    borderRadius: 10,
    borderColor: "#24211E",
    borderWidth: 1,
    cardStyle: "bordered",
    headerBg: "#141210",
    headerBorder: "#24211E",
  },
};

const adhdOcean = {
  id: "adhd-ocean",
  name: "Ocean Breeze",
  category: "adhd",
  premium: false,
  description: "Dark cool blue — clear mind, reduced noise",
  preview: { bg: "#0C1420", accent: "#0369A1", accent2: "#0D9488" },
  tokens: {
    isDark: true,
    // Bundle H — contrast bump. See adhd-calm comment.
    textPrimary: "#F5F7FA",
    textSecondary: "#CBD5E1",
    textMuted: "#64748B",
    success: "#059669",
    danger: "#DC2626",
    warning: "#D97706",
    glowIntensity: 0,
    scanlines: false,
    progressStyle: "solid",
    buttonStyle: "filled",
    fontHeading: "'Lexend', sans-serif",
    fontBody: "'Lexend', sans-serif",
    fontScale: 1.15,
    bgPrimary: "#0C1420",
    bgSecondary: "#111C2C",
    bgTertiary: "#182438",
    bgCard: "#111C2C",
    accent: "#0369A1",
    accent2: "#0D9488",
    accent3: "#EC4899",
    accent4: "#F59E0B",
    spacing: 1.2,
    borderRadius: 10,
    borderColor: "#182438",
    borderWidth: 1,
    cardStyle: "bordered",
    headerBg: "#0C1420",
    headerBorder: "#182438",
  },
};

// =====================================================
// THEME REGISTRY
// =====================================================

// Style type mapping: determines component variants (game=CRT/glow, professional=clean/minimal, minimal=flat/calm, adhd=focus/calm)
const STYLE_TYPE_MAP = {
  "mmntm-dark": "professional", "mmntm-light": "professional", "mmntm-light-true": "professional",
  "midnight-purple": "professional", "deep-navy": "professional", "forest": "professional",
  "retro-cyber": "game", "neon-arcade": "game", "hacker-ops": "game", "synthwave": "game",
  "warm-vintage": "minimal", "industrial": "game", "gaming-hud": "game", "calm-focus": "minimal",
  "minimal-light": "minimal", "exec-dark-tech": "professional",
  "corporate-blue": "professional", "executive-dark": "professional", "neutral-workspace": "professional",
  "soft-productivity": "professional", "office-classic": "professional", "slate-pro": "professional",
  "green-ledger": "professional", "focus-whiteboard": "professional", "calm-corporate": "professional",
  "high-contrast": "professional",
  "minimalist-bw": "minimal", "minimalist-bw-dark": "minimal",
  "adhd-calm": "adhd", "adhd-warm": "adhd", "adhd-ocean": "adhd",
};

export const THEME_PRESETS = [
  // MMNTM Core — curated 7
  mmntmDark, mmntmLightTrue, minimalistBW, minimalistBWLight,
  midnightPurple, deepNavy, forest,
  // Slate variant kept for legacy users
  mmntmLight,
  // Creative
  retroCyber, neonArcade, hackerOps, synthwave, warmVintage,
  industrial, gamingHud, calmFocus, minimalLight, execDarkTech,
  // Professional
  corporateBlue, executiveDark, neutralWorkspace, softProductivity,
  officeClassic, slatePro, greenLedger, focusWhiteboard, calmCorporate, highContrast,
  // ADHD
  adhdCalm, adhdWarm, adhdOcean,
].map((t) => ({
  ...t,
  styleType: STYLE_TYPE_MAP[t.id] || "game",
  levelRequired: THEME_LEVEL_MAP[t.id] ?? 99,
}));

// =====================================================
// CURATED CORE 5 — shown in Appearance settings default picker
// Deliberately consolidated from 7 → 5 (Feb 2026):
//   • "Dark"  uses the true-black minimalist-bw tokens (was: mmntm-dark)
//   • "Light" uses the true-white minimalist-bw-dark tokens (was: mmntm-light-true)
//   • Black Mono / White Mono removed — they ARE the new Dark / Light.
// Legacy ids are mapped via THEME_ID_MIGRATIONS so existing users keep their look.
// =====================================================
export const CURATED_CORE_THEMES = [
  { id: "minimalist-bw",      label: "Dark",            swatch: "#000000", accent: "#FFFFFF" },
  { id: "minimalist-bw-dark", label: "Light",           swatch: "#FFFFFF", accent: "#000000" },
  { id: "midnight-purple",    label: "Midnight Purple", swatch: "#0F0A1A", accent: "#A855F7" },
  { id: "deep-navy",          label: "Deep Navy",       swatch: "#0B0F19", accent: "#3B82F6" },
  { id: "forest",             label: "Forest",          swatch: "#0A1410", accent: "#22C55E" },
];

export const THEME_CATEGORIES = [
  { key: "mmntm", label: "MMNTM Core" },
  { key: "creative", label: "Creative" },
  { key: "professional", label: "Professional" },
  { key: "minimalist", label: "Minimalist" },
  { key: "adhd", label: "ADHD Focus" },
  { key: "custom", label: "My Themes" },
];

// Box color variants for light themes (professional palette)
export const BOX_COLORS_LIGHT = {
  green:  { screen: "#f0fdf4", text: "#2F855A", dim: "#6B7280", glow: "none", border: "#bbf7d0" },
  amber:  { screen: "#fffbeb", text: "#B7791F", dim: "#6B7280", glow: "none", border: "#fde68a" },
  cyan:   { screen: "#ecfeff", text: "#2C7A7B", dim: "#6B7280", glow: "none", border: "#a5f3fc" },
  pink:   { screen: "#fdf2f8", text: "#9B2C6B", dim: "#6B7280", glow: "none", border: "#fbcfe8" },
  purple: { screen: "#faf5ff", text: "#4C51BF", dim: "#6B7280", glow: "none", border: "#e9d5ff" },
  orange: { screen: "#fff7ed", text: "#B7791F", dim: "#6B7280", glow: "none", border: "#fed7aa" },
  yellow: { screen: "#fefce8", text: "#8B6914", dim: "#6B7280", glow: "none", border: "#fef08a" },
  white:  { screen: "#f9fafb", text: "#4A5568", dim: "#9CA3AF", glow: "none", border: "#e5e7eb" },
};

// Professional dark box colors — muted, corporate-friendly, no glow
export const BOX_COLORS_PRO_DARK = {
  green:  { screen: "#0f1a14", text: "#2F855A", dim: "#276749", glow: "none", border: "#1a3a27" },
  amber:  { screen: "#1a170f", text: "#B7791F", dim: "#8B6914", glow: "none", border: "#3a3017" },
  cyan:   { screen: "#0f171a", text: "#2C7A7B", dim: "#1D5C5D", glow: "none", border: "#17303a" },
  pink:   { screen: "#1a0f17", text: "#9B5B7A", dim: "#7B4561", glow: "none", border: "#3a1730" },
  purple: { screen: "#150f1a", text: "#4C51BF", dim: "#3B3F99", glow: "none", border: "#2e1741" },
  orange: { screen: "#1a130f", text: "#B7791F", dim: "#8B6914", glow: "none", border: "#3a2517" },
  yellow: { screen: "#1a190f", text: "#D69E2E", dim: "#A67C1E", glow: "none", border: "#3a3517" },
  white:  { screen: "#111316", text: "#9CA3AF", dim: "#6B7280", glow: "none", border: "#1e293b" },
};

// Minimal box colors - very subdued
export const BOX_COLORS_MINIMAL = {
  green: { screen: "#0f1612", text: "#86efac", dim: "#4ade8040", glow: "none", border: "#1c2e22" },
  amber: { screen: "#16140f", text: "#fcd34d", dim: "#fbbf2440", glow: "none", border: "#2e2a1c" },
  cyan: { screen: "#0f1416", text: "#67e8f9", dim: "#22d3ee40", glow: "none", border: "#1c2a2e" },
  pink: { screen: "#160f14", text: "#f9a8d4", dim: "#f472b640", glow: "none", border: "#2e1c2a" },
  purple: { screen: "#130f16", text: "#d8b4fe", dim: "#a78bfa40", glow: "none", border: "#261c2e" },
  orange: { screen: "#16120f", text: "#fdba74", dim: "#fb923c40", glow: "none", border: "#2e221c" },
  yellow: { screen: "#16150f", text: "#fde047", dim: "#facc1540", glow: "none", border: "#2e2c1c" },
  white: { screen: "#111213", text: "#94a3b8", dim: "#64748b40", glow: "none", border: "#1e2028" },
};

// Minimalist B&W box colors — strict black/white/grey only
export const BOX_COLORS_BW_DARK = {
  green:  { screen: "#0A0A0A", text: "#FFFFFF", dim: "#666666", glow: "none", border: "#333333" },
  amber:  { screen: "#0A0A0A", text: "#CCCCCC", dim: "#555555", glow: "none", border: "#333333" },
  cyan:   { screen: "#0A0A0A", text: "#FFFFFF", dim: "#666666", glow: "none", border: "#333333" },
  pink:   { screen: "#0A0A0A", text: "#AAAAAA", dim: "#555555", glow: "none", border: "#333333" },
  purple: { screen: "#0A0A0A", text: "#DDDDDD", dim: "#666666", glow: "none", border: "#333333" },
  orange: { screen: "#0A0A0A", text: "#BBBBBB", dim: "#555555", glow: "none", border: "#333333" },
  yellow: { screen: "#0A0A0A", text: "#EEEEEE", dim: "#666666", glow: "none", border: "#333333" },
  white:  { screen: "#0A0A0A", text: "#FFFFFF", dim: "#777777", glow: "none", border: "#333333" },
};

export const BOX_COLORS_BW_LIGHT = {
  green:  { screen: "#F5F5F5", text: "#000000", dim: "#999999", glow: "none", border: "#E0E0E0" },
  amber:  { screen: "#F5F5F5", text: "#333333", dim: "#AAAAAA", glow: "none", border: "#E0E0E0" },
  cyan:   { screen: "#F5F5F5", text: "#000000", dim: "#999999", glow: "none", border: "#E0E0E0" },
  pink:   { screen: "#F5F5F5", text: "#555555", dim: "#AAAAAA", glow: "none", border: "#E0E0E0" },
  purple: { screen: "#F5F5F5", text: "#222222", dim: "#999999", glow: "none", border: "#E0E0E0" },
  orange: { screen: "#F5F5F5", text: "#444444", dim: "#AAAAAA", glow: "none", border: "#E0E0E0" },
  yellow: { screen: "#F5F5F5", text: "#111111", dim: "#999999", glow: "none", border: "#E0E0E0" },
  white:  { screen: "#F5F5F5", text: "#000000", dim: "#888888", glow: "none", border: "#E0E0E0" },
};

// ADHD box colors — warm, calming, high contrast, soft
export const BOX_COLORS_ADHD = {
  green:  { screen: "#F0FDF4", text: "#059669", dim: "#6EE7B7", glow: "none", border: "#A7F3D0" },
  amber:  { screen: "#FFFBEB", text: "#D97706", dim: "#FCD34D", glow: "none", border: "#FDE68A" },
  cyan:   { screen: "#F0FDFA", text: "#0D9488", dim: "#5EEAD4", glow: "none", border: "#99F6E4" },
  pink:   { screen: "#FDF2F8", text: "#DB2777", dim: "#F9A8D4", glow: "none", border: "#FBCFE8" },
  purple: { screen: "#FAF5FF", text: "#7C3AED", dim: "#C4B5FD", glow: "none", border: "#DDD6FE" },
  orange: { screen: "#FFF7ED", text: "#EA580C", dim: "#FDBA74", glow: "none", border: "#FED7AA" },
  yellow: { screen: "#FEFCE8", text: "#CA8A04", dim: "#FDE047", glow: "none", border: "#FEF08A" },
  white:  { screen: "#FAFAF9", text: "#57534E", dim: "#A8A29E", glow: "none", border: "#E7E5E4" },
};

